import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui-helpers";
import { Switch } from "@/components/ui/switch";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export function AiToggleCard() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = !!user?.ai_enabled;
  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.put("/settings", { ai_enabled: next });
      await refreshUser();
      toast.success(next ? "AI features turned on" : "AI features turned off");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 mb-5 flex items-start gap-4" data-testid="ai-toggle-card">
      <span className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-primary" /></span>
      <div className="flex-1 min-w-0">
        <h3 className="font-head font-bold text-slate-900">AI features</h3>
        <p className="text-xs text-slate-500 mt-0.5">Lead scoring, service suggestions, message triage, case-note flagging and receipt reading. These use AI credits. Saved results stay visible when AI is off.</p>
        <div className={`mt-2 text-xs font-semibold ${on ? "text-emerald-600" : "text-slate-400"}`} data-testid="ai-toggle-status">{on ? "On — using AI credits" : "Off — no AI credits used"}</div>
      </div>
      <Switch data-testid="ai-toggle-switch" checked={on} disabled={busy} onCheckedChange={toggle} />
    </Card>
  );
}

export function NoteEditingCard() {
  const [val, setVal] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setVal(r.data.note_editing || "nobody")).catch(() => setVal("nobody")); }, []);
  const change = async (v) => {
    const prev = val; setVal(v);
    try { await api.put("/settings", { note_editing: v }); toast.success("Note editing updated"); } catch (e) { setVal(prev); toast.error(apiErr(e)); }
  };
  if (!val) return null;
  return (
    <Card className="p-5 mb-5" data-testid="note-editing-card">
      <h3 className="font-head font-bold text-slate-900">Note editing</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">Who can edit or delete client notes after they're saved. Edits show "Edited by" and deletions are recorded in the Activity Log.</p>
      <div className="inline-flex flex-wrap bg-slate-100 rounded-xl p-1">
        {[["nobody", "Nobody (permanent)"], ["admins", "Admins only"], ["writer_admins", "Writer and admins"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`note-editing-${k}`} onClick={() => change(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${val === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
    </Card>
  );
}
